import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RefreshCcw, Clock, UserPlus } from "lucide-react";
import { toast } from "sonner";

const fmtH = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(1)} h`);
const MATCH_TITLES = {
  matched_ahv: "Automatisch über AHV-Nr. verknüpft",
  matched_email: "Automatisch über E-Mail verknüpft",
  matched_name: "Automatisch über Namen verknüpft",
  auto_created: "Mitarbeiter wurde aus den CRM-Daten automatisch angelegt – Stammdaten ergänzen",
};

export function WorktimeSummariesCard({ companyId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const r = await apiClient.get(`/companies/${companyId}/worktime-summaries`);
      setItems(r.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const reprocess = async () => {
    setReprocessing(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/worktime-summaries/reprocess`);
      const d = r.data || {};
      const created = (d.payroll || []).filter((p) => p.action === "run_created").length;
      const bonusRuns = ((d.bonuses || {}).payroll || []).filter((p) => p.payroll_run_id).length;
      toast.success(`${d.matched || 0} zugeordnet · ${d.created_employees || 0} Mitarbeiter angelegt · ${created} Lohnlauf-Entwürfe erstellt · ${bonusRuns} GF-Boni in Lohnläufe übernommen`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nachverarbeitung fehlgeschlagen");
    } finally {
      setReprocessing(false);
    }
  };

  const openCount = items.filter((w) => !w.employee_id || !w.payroll_run_id).length;

  if (!loading && items.length === 0) return null;

  return (
    <Card className="p-4 border border-slate-200 bg-white shadow-sm" data-testid="worktime-summaries-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-emerald-700" />
          <h3 className="text-sm font-semibold text-slate-800">Stunden aus der Partner-App (StrongFit, ReinMove …)</h3>
          <span className="text-xs text-slate-400">automatisch am 1. des Monats übermittelt</span>
        </div>
        <div className="flex items-center gap-2">
          {openCount > 0 && (
            <Button variant="outline" size="sm" onClick={reprocess} disabled={reprocessing} title="Nicht zugeordnete Stunden erneut verarbeiten: Mitarbeiter aus den CRM-Daten anlegen und Lohnlauf-Entwürfe erzeugen" data-testid="worktime-reprocess-button">
              <UserPlus className={`h-3.5 w-3.5 mr-1 ${reprocessing ? "animate-pulse" : ""}`} />
              Nachverarbeiten ({openCount})
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={load} disabled={loading} title="Stunden-Übersicht neu laden" data-testid="worktime-refresh-button">
            <RefreshCcw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
            Aktualisieren
          </Button>
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Monat</TableHead>
            <TableHead>Trainer</TableHead>
            <TableHead>Mitarbeiter (ERP)</TableHead>
            <TableHead className="text-right">Soll</TableHead>
            <TableHead className="text-right">Ist</TableHead>
            <TableHead className="text-right">Überstunden</TableHead>
            <TableHead className="text-right">Saldo</TableHead>
            <TableHead className="text-right">Ferien / Krank</TableHead>
            <TableHead>Lohnlauf</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((w) => (
            <TableRow key={w.id} data-testid={`worktime-row-${w.external_id}`}>
              <TableCell className="text-sm">{w.period_label || w.period || "—"}</TableCell>
              <TableCell className="text-sm">
                <div>{w.trainer_name || "—"}</div>
                <div className="text-[11px] text-slate-400">{w.trainer_email || ""}</div>
              </TableCell>
              <TableCell>
                {w.employee_id ? (
                  <Badge className={w.match_status === "auto_created" ? "bg-sky-100 text-sky-800" : "bg-emerald-100 text-emerald-800"} title={MATCH_TITLES[w.match_status] || "Verknüpft"}>
                    {w.employee_name || "verknüpft"}{w.match_status === "auto_created" ? " · neu angelegt" : ""}
                  </Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-800" title="Kein Mitarbeiter gefunden und keine Lohndaten (Lohn/Stunden = 0) – wird nicht automatisch angelegt">
                    nicht verknüpft
                  </Badge>
                )}
              </TableCell>
              <TableCell className="text-right text-sm">{fmtH(w.target_hours)}</TableCell>
              <TableCell className="text-right text-sm font-medium">{fmtH(w.actual_hours)}</TableCell>
              <TableCell className={`text-right text-sm ${Number(w.overtime_hours) < 0 ? "text-rose-600" : "text-emerald-700"}`}>
                {w.overtime_hours === null || w.overtime_hours === undefined ? "—" : `${Number(w.overtime_hours) >= 0 ? "+" : ""}${Number(w.overtime_hours).toFixed(1)} h`}
              </TableCell>
              <TableCell className="text-right text-sm">{fmtH(w.balance_hours)}</TableCell>
              <TableCell className="text-right text-sm">
                {(w.vacation_days ?? 0)} / {(w.sick_days ?? 0)}
              </TableCell>
              <TableCell>
                {w.payroll_run_id ? (
                  <Badge className="bg-emerald-100 text-emerald-800" title="Lohnlauf-Entwurf wurde automatisch berechnet — unten prüfen und buchen">
                    Entwurf erstellt
                  </Badge>
                ) : (
                  <span className="text-xs text-slate-400">—</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
